import { create } from 'zustand';
import { artifactApi } from '../services/api';
import type { VersionedArtifact, ArtifactEvent, TestResult } from '../services/api';

interface ArtifactStore {
  artifacts: VersionedArtifact[];
  activeArtifact: VersionedArtifact | null;
  versions: VersionedArtifact[];
  events: ArtifactEvent[];
  testResult: TestResult | null;
  testing: boolean;
  error: string | null;
  /**
   * Per-product env selection (e.g. { cvie: 'cvie-stg' }) — set by EnvSwitcherWidget. Persisted
   * per journal in localStorage (`maestro.envswitch.<journalRootId>`) so an F5 or an MSAL redirect
   * does not drop it: before 2026-10-21 this lived only in memory, so the switcher's "applies to this
   * journal only" was neither per-journal nor durable, and the copilot / runTest silently fell back
   * to defaults after every reload — which read as the copilot losing context.
   */
  activeDataSources: Record<string, string>;
  /** The journal whose selection is currently loaded (null until a widget mounts with one). */
  envSwitchJournalId: string | null;
  loadActiveDataSources: (journalRootId: string | undefined) => void;
  setActiveDataSource: (product: string, sourceId: string | null) => void;
  /** Live mirror of the SQL editor buffer — shared with the Copilot panel (KB Doc 139) */
  editorBuffer: string;
  setEditorBuffer: (sql: string) => void;
  /** When set, SqlEditorWidget loads this SQL into CodeMirror (Copilot draft Load action) */
  pendingEditorLoad: string | null;
  requestEditorLoad: (sql: string | null) => void;

  loadArtifacts: (plane?: string) => Promise<void>;
  selectArtifact: (artifactId: string) => Promise<void>;
  loadVersions: (artifactId: string) => Promise<void>;
  loadEvents: (artifactId: string) => Promise<void>;
  runTest: (artifactId: string, params: Record<string, unknown>, sqlOverride?: string) => Promise<void>;
  saveVersion: (artifactId: string, content: unknown, changeNote: string) => Promise<void>;
  promote: (artifactId: string, versionId: string) => Promise<void>;
  rollback: (artifactId: string) => Promise<void>;
  clearTest: () => void;
}

const envSwitchKey = (journalRootId: string) => `maestro.envswitch.${journalRootId}`;

function readEnvSwitch(journalRootId: string): Record<string, string> {
  try {
    const raw = window.localStorage.getItem(envSwitchKey(journalRootId));
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return Object.fromEntries(Object.entries(parsed as Record<string, unknown>).filter(([, v]) => typeof v === 'string')) as Record<string, string>;
  } catch {
    return {};
  }
}

function writeEnvSwitch(journalRootId: string | null, selection: Record<string, string>): void {
  if (!journalRootId) return; // no journal yet — keep the in-memory behaviour, nothing to key on
  try {
    if (Object.keys(selection).length === 0) window.localStorage.removeItem(envSwitchKey(journalRootId));
    else window.localStorage.setItem(envSwitchKey(journalRootId), JSON.stringify(selection));
  } catch {
    // storage unavailable (private mode, quota) — the selection still applies for this session
  }
}

export const useArtifactStore = create<ArtifactStore>((set, get) => ({
  artifacts: [],
  activeArtifact: null,
  versions: [],
  events: [],
  testResult: null,
  testing: false,
  error: null,
  activeDataSources: {},
  envSwitchJournalId: null,
  editorBuffer: '',
  setEditorBuffer: (sql) => set({ editorBuffer: sql }),
  pendingEditorLoad: null,
  requestEditorLoad: (sql) => set({ pendingEditorLoad: sql }),

  loadActiveDataSources: (journalRootId) => {
    if (!journalRootId || get().envSwitchJournalId === journalRootId) return;
    set({ envSwitchJournalId: journalRootId, activeDataSources: readEnvSwitch(journalRootId) });
  },

  setActiveDataSource: (product, sourceId) => {
    set(state => {
      const next = { ...state.activeDataSources };
      if (sourceId) next[product] = sourceId;
      else delete next[product];
      writeEnvSwitch(state.envSwitchJournalId, next);
      return { activeDataSources: next };
    });
  },

  loadArtifacts: async (plane) => {
    try {
      const artifacts = await artifactApi.list(plane);
      set({ artifacts, error: null });
    } catch (err: unknown) {
      set({ error: err instanceof Error ? err.message : String(err) });
    }
  },

  selectArtifact: async (artifactId) => {
    try {
      const artifact = await artifactApi.get(artifactId);
      set({ activeArtifact: artifact, error: null });
      get().loadVersions(artifactId);
      get().loadEvents(artifactId);
    } catch (err: unknown) {
      set({ error: err instanceof Error ? err.message : String(err) });
    }
  },

  loadVersions: async (artifactId) => {
    try {
      const versions = await artifactApi.versions(artifactId);
      set({ versions });
    } catch { /* ignore */ }
  },

  loadEvents: async (artifactId) => {
    try {
      const events = await artifactApi.events(artifactId);
      set({ events });
    } catch { /* ignore */ }
  },

  runTest: async (artifactId, params, sqlOverride) => {
    set({ testing: true, testResult: null, error: null });
    try {
      // Route to the env selected in the EnvSwitcher (per product), if any
      const artifact = get().activeArtifact;
      const ds = (artifact?.dataSource || '').toLowerCase();
      const product = ds.includes('redshift') || ds.includes('maxsense') ? 'maxsense' : 'cvie';
      const dataSourceKey = get().activeDataSources[product];
      const result = await artifactApi.test(artifactId, { params, sql: sqlOverride, dataSourceKey });
      set({ testResult: result, testing: false });
      get().loadEvents(artifactId);
    } catch (err: unknown) {
      set({ testing: false, error: err instanceof Error ? err.message : String(err) });
    }
  },

  saveVersion: async (artifactId, content, changeNote) => {
    try {
      const newVersion = await artifactApi.createVersion(artifactId, { content, changeNote });
      set({ activeArtifact: newVersion, error: null });
      get().loadVersions(artifactId);
      get().loadEvents(artifactId);
    } catch (err: unknown) {
      set({ error: err instanceof Error ? err.message : String(err) });
    }
  },

  promote: async (artifactId, versionId) => {
    try {
      await artifactApi.promote(artifactId, versionId);
      get().selectArtifact(artifactId);
      get().loadArtifacts();
    } catch (err: unknown) {
      set({ error: err instanceof Error ? err.message : String(err) });
    }
  },

  rollback: async (artifactId) => {
    try {
      await artifactApi.rollback(artifactId);
      get().selectArtifact(artifactId);
      get().loadArtifacts();
    } catch (err: unknown) {
      set({ error: err instanceof Error ? err.message : String(err) });
    }
  },

  clearTest: () => set({ testResult: null }),
}));
